import { makeStyles } from '@material-ui/styles'
import { blockStorageConfigDoc } from 'app/docLinks'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import Theme from 'core/themes/model'
import { customValidator } from 'core/utils/fieldValidators'
import { update, without } from 'ramda'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { trackEvent } from 'utils/tracking'
import { listVolumeTypes } from '../../../storage/volume-types/actions'
import { volumeTypesSelectorFiltered } from '../../../storage/volume-types/selectors'
import LearnMoreLink from 'core/components/LearnMoreLink'
import StorageTypeLogos from '../StorageTypeLogos'
import AddVolumeBackend from './AddVolumeBackend'
import { useVolumeTypesByBackend, VolumeType } from './utils/volumeTypeMapping'
import VolumeBackendAccordion from './VolumeBackendAccordion'

const useStyles = makeStyles<Theme>((theme) => ({
  cardBody: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  accordionList: {
    display: 'grid',
    gap: theme.spacing(2),
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(2),
  },
}))

export default function StorageVolumeTypeTable({
  params,
  updateParams,
  getParamsUpdater,
  usedVolumeBackends,
  existingVolumeBackends = {},
  storageVolumeErrorIndexes = [],
  setStorageVolumeErrorIndexes,
}) {
  const classes = useStyles()
  const { loading: loadingVolumeTypes } = useListAction(listVolumeTypes)
  const existingVolumeTypes = useSelector(volumeTypesSelectorFiltered) as VolumeType[]

  // Build a memoized map of backend names to their assigned volume types
  const { getAssignedTypes, loading: volumeTypesLoading } = useVolumeTypesByBackend(
    [...existingVolumeTypes, ...params?.volumeTypes],
    loadingVolumeTypes,
  )

  const removeVolumeBackendFn = useCallback(
    (type) => {
      updateParams({ volumeBackends: without([type], params.volumeBackends) })
      trackEvent(`Cluster Blueprint - Clicked Remove Volume Type`)
    },
    [params.volumeBackends],
  )

  const uniqueVolumeTypeNameValidator = customValidator((name) => {
    const matchingVolumeTypeNames = params.volumeBackends
      ?.map((type) => type.type)
      ?.filter((type) => type === name)
    return matchingVolumeTypeNames.length < 2
  }, `Volume Backend name must be unique`)

  const updateVolumeBackendName = useCallback(
    (name, id, idx) => {
      const updatedVolumeBackendConfigs = params.volumeBackendConfigs.map((config) => {
        return config.volumeBackendId === id
          ? {
              ...config,
              volumeBackendName: name,
            }
          : config
      })
      updateParams({
        volumeBackends: update(idx, { type: name, id }, params.volumeBackends),
        volumeBackendConfigs: updatedVolumeBackendConfigs,
      })
    },
    [params.volumeBackends, params.volumeBackendConfigs],
  )

  return (
    <FormFieldCard title="Storage Volume Backend" fullWidth>
      <div className={classes.cardBody}>
        <Text variant="body2">
          Register Volume Types to enable the use of Persistent Storage Volumes. Supported
          infrastructure integrations include. <LearnMoreLink url={blockStorageConfigDoc} />
        </Text>
        <StorageTypeLogos />
      </div>
      <div className={classes.cardBody}>
        <div className={classes.accordionList}>
          {params?.volumeBackends?.map((volumeBackend, idx) => (
            <VolumeBackendAccordion
              key={volumeBackend.id}
              params={params}
              updateParams={updateParams}
              getParamsUpdater={getParamsUpdater}
              volumeBackend={volumeBackend}
              idx={idx}
              usedVolumeBackends={usedVolumeBackends}
              uniqueVolumeTypeNameValidator={uniqueVolumeTypeNameValidator}
              onDelete={removeVolumeBackendFn}
              onUpdateName={updateVolumeBackendName}
              assignedVolumeTypes={getAssignedTypes(volumeBackend.type)}
              loadingVolumeTypes={volumeTypesLoading}
              isExistingVolumeBackend={Object.keys(existingVolumeBackends)?.includes(
                volumeBackend.type,
              )}
              hasError={storageVolumeErrorIndexes?.includes(idx)}
              setStorageVolumeErrorIndexes={setStorageVolumeErrorIndexes}
            />
          ))}
        </div>
        <div className={classes.buttonRow}>
          <AddVolumeBackend
            params={params}
            title="Add Volume Backend"
            updateParams={updateParams}
          />
        </div>
      </div>
    </FormFieldCard>
  )
}
